import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Gavel, Clock, Trophy, TrendingUp, Star, ChevronRight, Send, LogIn, UserPlus, LogOut, Sparkles, ShieldCheck, CheckCircle2 } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import {
  bidderRegister, bidderLogin, bidderMe, bidderLogout, bidderDashboard,
  fetchCurrentAuction, fetchAuctionBids, fetchPastWinners, placeBid, getBidderToken,
} from "@/lib/api";

// ---- Countdown helper ------------------------------------------------------

const useCountdown = (secondsRemaining) => {
  const [secs, setSecs] = useState(secondsRemaining || 0);
  useEffect(() => {
    setSecs(secondsRemaining || 0);
    if (!secondsRemaining) return;
    const id = setInterval(() => setSecs((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [secondsRemaining]);
  const d = Math.floor(secs / 86400);
  const h = Math.floor((secs % 86400) / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  return { d, h, m, s, total: secs };
};

const Pip = ({ n, label }) => (
  <div className="flex flex-col items-center px-3 sm:px-5 py-3 bg-[#0F172A] text-white rounded-xl min-w-[64px]">
    <div className="text-2xl sm:text-3xl font-bold tabular-nums">{String(n).padStart(2, "0")}</div>
    <div className="text-[9px] uppercase tracking-widest text-[#94A3B8] mt-0.5">{label}</div>
  </div>
);

// ---- Hook to read bidder session ------------------------------------------

const useBidderSession = () => {
  const [bidder, setBidder] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const refresh = useCallback(() => {
    if (!getBidderToken()) { setBidder(null); setLoaded(true); return Promise.resolve(null); }
    return bidderMe().then((d) => { setBidder(d); setLoaded(true); return d; }).catch(() => { setBidder(null); setLoaded(true); return null; });
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  return { bidder, loaded, refresh, logout: () => { bidderLogout(); setBidder(null); } };
};

// ---- Auth panel (toggle login/register) -----------------------------------

const AuthPanel = ({ onAuthed, isNl }) => {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "", company: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      if (mode === "login") await bidderLogin({ email: form.email, password: form.password });
      else await bidderRegister(form);
      onAuthed();
    } catch (e2) { setErr(e2.response?.data?.detail || e2.message); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="bg-white border-2 border-amber-300 rounded-2xl p-6 max-w-md mx-auto" data-testid="auction-auth-panel">
      <div className="flex gap-1.5 mb-5 p-1 bg-[#F1F5F9] rounded-lg">
        <button type="button" onClick={() => setMode("login")} className={`flex-1 py-2 text-sm font-medium rounded-md ${mode === "login" ? "bg-white shadow text-[#0F172A]" : "text-[#64748B]"}`} data-testid="auth-mode-login"><LogIn size={14} className="inline mr-1" /> {isNl ? "Inloggen" : "Login"}</button>
        <button type="button" onClick={() => setMode("register")} className={`flex-1 py-2 text-sm font-medium rounded-md ${mode === "register" ? "bg-white shadow text-[#0F172A]" : "text-[#64748B]"}`} data-testid="auth-mode-register"><UserPlus size={14} className="inline mr-1" /> {isNl ? "Registreren" : "Sign up"}</button>
      </div>

      {mode === "register" && (
        <>
          <div className="mb-3"><label className="overline block mb-1 text-[#64748B]">{isNl ? "Naam" : "Name"}</label><input className="hac-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="auth-name" /></div>
          <div className="mb-3"><label className="overline block mb-1 text-[#64748B]">{isNl ? "Bedrijf" : "Company"}</label><input className="hac-input" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="auth-company" /></div>
        </>
      )}
      <div className="mb-3"><label className="overline block mb-1 text-[#64748B]">E-mail</label><input type="email" required className="hac-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="auth-email" />
        <p className="text-[11px] text-emerald-700 mt-1 inline-flex items-center gap-1"><ShieldCheck size={11} /> {isNl ? "Andere bieders zien alleen de 1e letter — bv. 'm•••'." : "Other bidders see only the first letter — e.g. 'm•••'."}</p>
      </div>
      <div className="mb-4"><label className="overline block mb-1 text-[#64748B]">{isNl ? "Wachtwoord" : "Password"} ({isNl ? "min 8 tekens" : "min 8 chars"})</label><input type="password" required minLength={8} className="hac-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="auth-password" /></div>
      {err && <div className="mb-3 text-xs text-red-600 bg-red-50 border border-red-200 p-2 rounded" data-testid="auth-error">{err}</div>}
      <button type="submit" disabled={busy} className="hac-btn-primary w-full bg-amber-500 hover:bg-amber-600 border-amber-500 disabled:opacity-60" data-testid="auth-submit">
        {busy ? "…" : (mode === "login" ? (isNl ? "Inloggen" : "Login") : (isNl ? "Account aanmaken" : "Create account"))}
      </button>
      <p className="text-[11px] text-[#94A3B8] mt-4 text-center">{isNl ? "Account voor app-eigenaren die op advertentieposities willen bieden." : "Account for app owners who want to bid on advertising positions."}</p>
    </form>
  );
};

// ---- Auction page ----------------------------------------------------------

const AuctionPage = () => {
  const { lang } = useLang();
  const isNl = lang === "nl";
  const navigate = useNavigate();
  const [auction, setAuction] = useState(null);
  const [bids, setBids] = useState([]);
  const [pastWinners, setPastWinners] = useState([]);
  const [bidAmount, setBidAmount] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { bidder, refresh } = useBidderSession();
  const polling = useRef(null);

  const load = useCallback(() => {
    fetchCurrentAuction().then((a) => {
      setAuction(a);
      if (a) {
        setBidAmount(String(a.min_next_bid_eur));
        fetchAuctionBids(a.id).then(setBids).catch(() => setBids([]));
      }
    }).catch(() => setAuction(null));
  }, []);

  useEffect(() => {
    load();
    document.title = isNl ? "Wekelijkse Ad Auction — HorseAppCompare" : "Weekly Ad Auction — HorseAppCompare";
    fetchPastWinners(8).then(setPastWinners).catch(() => setPastWinners([]));
    polling.current = setInterval(load, 15000);
    return () => clearInterval(polling.current);
  }, [load, isNl]);

  const countdown = useCountdown(auction?.seconds_remaining);

  const onBid = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const res = await placeBid(auction.id, parseFloat(bidAmount));
      setAuction(res.auction);
      setBidAmount(String(res.auction.min_next_bid_eur));
      fetchAuctionBids(auction.id).then(setBids);
    } catch (e2) { setError(e2.response?.data?.detail || e2.message); }
    finally { setBusy(false); }
  };

  if (!auction) {
    return <main className="hac-container py-20 text-center text-[#94A3B8]">Loading…</main>;
  }

  const statusMap = isNl ? {
    open: "Veiling is open", almost_closed: "Sluit bijna!", scheduled: "Binnenkort", closed: "Gesloten", won: "Toegewezen",
  } : {
    open: "Auction is open", almost_closed: "Closing soon", scheduled: "Scheduled", closed: "Closed", won: "Awarded",
  };
  const statusColor = {
    open: "bg-emerald-100 text-emerald-700",
    almost_closed: "bg-amber-100 text-amber-700",
    scheduled: "bg-blue-100 text-blue-700",
    closed: "bg-[#F1F5F9] text-[#64748B]",
    won: "bg-purple-100 text-purple-700",
  };

  const isMyBidHighest = bidder && auction.current_highest_bidder_email === bidder.email;
  const isActive = auction.status === "open" || auction.status === "almost_closed";

  return (
    <main data-testid="auction-page">
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#0F172A] via-[#1e293b] to-[#0F172A] text-white">
        <div className="hac-container py-16 max-w-5xl">
          <div className="flex items-start gap-2 mb-4">
            <Gavel size={20} className="text-amber-400 mt-1" />
            <span className="text-amber-400 text-xs uppercase tracking-widest font-bold">{isNl ? "Wekelijkse veiling" : "Weekly auction"}</span>
            <span className={`text-[10px] uppercase font-mono px-2 py-0.5 rounded ${statusColor[auction.status]}`} data-testid="auction-status">{statusMap[auction.status]}</span>
          </div>
          <h1 className="font-heading text-4xl md:text-6xl leading-[1.05] mb-4">
            {isNl ? "Word de Featured Equestrian App van de week" : "Become the Featured Equestrian App of the Week"}
          </h1>
          <p className="text-lg text-[#94A3B8] leading-relaxed max-w-3xl mb-8">
            {isNl
              ? "Bied op de premium advertentiepositie deze week op HorseAppCompare. Slechts één app wordt elke week uitgelicht."
              : "Place your bid for this week's premium advertising position on HorseAppCompare. Only one app will be featured each week."}
          </p>

          {/* Countdown */}
          <div className="bg-white/5 border border-white/10 backdrop-blur rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-3 text-xs uppercase tracking-widest text-[#94A3B8]">
              <Clock size={12} /> {isNl ? "Veiling sluit over" : "Auction closes in"}
            </div>
            <div className="flex flex-wrap gap-2" data-testid="auction-countdown">
              <Pip n={countdown.d} label={isNl ? "dagen" : "days"} />
              <Pip n={countdown.h} label={isNl ? "uren" : "hours"} />
              <Pip n={countdown.m} label={isNl ? "min" : "min"} />
              <Pip n={countdown.s} label={isNl ? "sec" : "sec"} />
            </div>
          </div>
        </div>
      </section>

      {/* Main grid */}
      <section className="hac-container py-12 max-w-6xl grid lg:grid-cols-3 gap-6">
        {/* Bid panel */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6">
            <div className="grid sm:grid-cols-3 gap-4 mb-6">
              <div className="bg-[#F8FAFC] rounded-xl p-4 border border-[#E2E8F0]">
                <div className="text-[10px] uppercase tracking-widest text-[#64748B]">{isNl ? "Richtprijs" : "List price"}</div>
                <div className="text-2xl font-bold text-[#0F172A] mt-1">€{((auction.list_price_eur ?? auction.starting_price_eur) || 0).toFixed(0)}</div>
                <div className="text-[10px] text-[#94A3B8]">{isNl ? `per week · bieden vanaf €${(auction.starting_price_eur || 0).toFixed(0)}` : `per week · bid from €${(auction.starting_price_eur || 0).toFixed(0)}`}</div>
              </div>
              <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                <div className="text-[10px] uppercase tracking-widest text-amber-700">{isNl ? "Hoogste bod" : "Current top bid"}</div>
                <div className="text-2xl font-bold text-amber-900 mt-1" data-testid="auction-highest">{auction.current_highest_bid_eur != null ? `€${auction.current_highest_bid_eur.toFixed(0)}` : "—"}</div>
                <div className="text-[10px] text-amber-700">{auction.bid_count} {isNl ? "biedingen" : "bids"}</div>
              </div>
              <div className="bg-[#F8FAFC] rounded-xl p-4 border border-[#E2E8F0]">
                <div className="text-[10px] uppercase tracking-widest text-[#64748B]">{isNl ? "Min. volgend bod" : "Min next bid"}</div>
                <div className="text-2xl font-bold text-[#0F172A] mt-1" data-testid="auction-min-next">€{auction.min_next_bid_eur.toFixed(0)}</div>
                <div className="text-[10px] text-[#94A3B8]">+€{auction.min_increment_eur.toFixed(0)} {isNl ? "increment" : "increment"}</div>
              </div>
            </div>

            {!isActive && (
              <div className="p-4 bg-[#F1F5F9] rounded-lg text-sm text-[#64748B] text-center" data-testid="auction-closed-notice">
                {auction.status === "won"
                  ? (isNl ? `Gewonnen door ${(auction.winner_email || "").charAt(0) || "?"}••• met €${auction.winner_amount_eur?.toFixed(0)}` : `Won by ${(auction.winner_email || "").charAt(0) || "?"}••• at €${auction.winner_amount_eur?.toFixed(0)}`)
                  : (isNl ? "Deze veiling is gesloten. De volgende start automatisch maandag 00:00 UTC." : "This auction is closed. Next one auto-starts Monday 00:00 UTC.")}
              </div>
            )}

            {isActive && (bidder ? (
              <form onSubmit={onBid} className="border-t border-[#E2E8F0] pt-5">
                {isMyBidHighest && (
                  <div className="mb-3 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-700 inline-flex items-center gap-1.5" data-testid="auction-am-leading">
                    <Trophy size={12} /> {isNl ? "Je hebt momenteel het hoogste bod" : "You currently have the highest bid"}
                  </div>
                )}
                <label className="overline block mb-1 text-[#64748B]">{isNl ? "Jouw bod (€)" : "Your bid (€)"}</label>
                <div className="flex gap-2">
                  <input type="number" min={auction.min_next_bid_eur} step="1" required className="hac-input flex-1" value={bidAmount} onChange={(e) => setBidAmount(e.target.value)} data-testid="auction-bid-input" />
                  <button type="submit" disabled={busy} className="hac-btn-primary bg-amber-500 hover:bg-amber-600 border-amber-500 disabled:opacity-60" data-testid="auction-bid-submit">
                    <Send size={14} /> {isMyBidHighest ? (isNl ? "Verhoog bod" : "Increase bid") : (isNl ? "Plaats bod" : "Place your bid")}
                  </button>
                </div>
                {error && <div className="mt-2 text-xs text-red-600 bg-red-50 border border-red-200 p-2 rounded" data-testid="auction-bid-error">{error}</div>}
                <p className="text-[11px] text-emerald-700 mt-2 inline-flex items-center gap-1" data-testid="bid-privacy-hint"><ShieldCheck size={11} /> {isNl ? "100% privé: andere bieders zien alleen 'm•••', niet je naam of bedrijf." : "100% private: other bidders see only 'm•••', not your name or company."}</p>
                <p className="text-[11px] text-[#94A3B8] mt-3 leading-relaxed">
                  {isNl
                    ? "Door te bieden ga je akkoord met onze redactionele review. Na winst krijg je een factuur per email. Na betaling gaat je advertentie live."
                    : "By bidding you agree to our editorial review. After winning you receive an invoice by email. Once paid, your ad goes live."}
                </p>
              </form>
            ) : (
              <div className="border-t border-[#E2E8F0] pt-6" data-testid="auction-requires-auth">
                <p className="text-sm text-[#64748B] text-center mb-4">{isNl ? "Maak een account of log in om te bieden." : "Create an account or log in to bid."}</p>
                <AuthPanel onAuthed={refresh} isNl={isNl} />
              </div>
            ))}
          </div>

          {/* Bid history */}
          {bids.length > 0 && (
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6">
              <h3 className="font-bold text-lg text-[#0F172A] mb-4 inline-flex items-center gap-2"><TrendingUp size={16} className="text-[#2563EB]" /> {isNl ? "Recente biedingen" : "Recent bids"}</h3>
              <table className="w-full">
                <thead className="text-[10px] uppercase tracking-widest text-[#64748B]">
                  <tr><th className="text-left py-2">{isNl ? "Bieder" : "Bidder"}</th><th className="text-right py-2">{isNl ? "Bedrag" : "Amount"}</th><th className="text-right py-2">{isNl ? "Tijdstip" : "Time"}</th></tr>
                </thead>
                <tbody>
                  {bids.map((b, i) => (
                    <tr key={i} className="border-t border-[#F1F5F9]">
                      <td className="py-2 text-sm font-mono text-[#64748B]" data-testid={`bid-anon-${i}`}>{b.anon_bidder}</td>
                      <td className={`py-2 text-right text-sm font-bold tabular-nums ${i === 0 ? "text-amber-700" : "text-[#0F172A]"}`}>€{b.amount_eur.toFixed(0)}</td>
                      <td className="py-2 text-right text-xs text-[#94A3B8]">{new Date(b.placed_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <aside className="space-y-4">
          {bidder && (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-widest text-amber-700 font-bold">{isNl ? "Ingelogd als" : "Logged in as"}</div>
                <button onClick={() => { bidderLogout(); refresh(); }} className="text-[10px] text-[#64748B] hover:text-red-600 inline-flex items-center gap-0.5" data-testid="bidder-logout"><LogOut size={10} /> {isNl ? "Uitloggen" : "Logout"}</button>
              </div>
              <div className="text-sm font-semibold text-[#0F172A] truncate">{bidder.name || bidder.email}</div>
              {bidder.company && <div className="text-xs text-[#64748B] truncate">{bidder.company}</div>}
              <Link to="/auction/dashboard" className="text-xs font-semibold text-amber-700 hover:underline inline-flex items-center gap-1 mt-3" data-testid="bidder-dashboard-link">{isNl ? "Mijn dashboard" : "My dashboard"} <ChevronRight size={11} /></Link>
            </div>
          )}

          <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5">
            <Star size={18} className="text-amber-500 fill-amber-500 mb-2" />
            <div className="text-xs uppercase tracking-widest text-[#64748B] font-bold mb-2">{isNl ? "Wat krijg je?" : "What you get"}</div>
            <ul className="space-y-2 text-sm text-[#0F172A]">
              <li className="flex gap-2"><span className="text-amber-500">●</span> {isNl ? "Homepage-banner — top positie" : "Homepage banner — top spot"}</li>
              <li className="flex gap-2"><span className="text-amber-500">●</span> {isNl ? "Featured op categoriepagina's" : "Featured on category pages"}</li>
              <li className="flex gap-2"><span className="text-amber-500">●</span> {isNl ? "\"Featured App of the Week\" badge" : '"Featured App of the Week" badge'}</li>
              <li className="flex gap-2"><span className="text-amber-500">●</span> {isNl ? "1 week zichtbaarheid bij paardeneigenaren" : "1 week visibility to horse owners actively comparing apps"}</li>
            </ul>
          </div>

          <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-2xl p-5">
            <ShieldCheck size={18} className="text-[#2563EB] mb-2" />
            <div className="text-xs uppercase tracking-widest text-[#64748B] font-bold mb-2">{isNl ? "Schaarste" : "Scarcity"}</div>
            <p className="text-sm text-[#0F172A] leading-relaxed">{isNl ? "Slechts 1 premium ad spot beschikbaar per week." : "Only 1 premium ad spot available per week."}</p>
          </div>

          <Link to="/premium-listing" className="block bg-white border border-[#E2E8F0] rounded-2xl p-5 hover:border-amber-300 transition-colors">
            <Sparkles size={18} className="text-amber-500 mb-2" />
            <div className="text-xs uppercase tracking-widest text-[#64748B] font-bold mb-2">{isNl ? "Te duur?" : "Too pricey?"}</div>
            <p className="text-sm text-[#0F172A] leading-relaxed">{isNl ? "Premium-listing vanaf €99/maand met permanente score-boost." : "Premium listing from €99/month with permanent score boost."}</p>
          </Link>

          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/media-kit.pdf`} target="_blank" rel="noreferrer" className="block bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-2xl p-5 transition-colors" data-testid="auction-mediakit-download">
            <div className="flex items-start gap-3">
              <div className="text-3xl shrink-0">📄</div>
              <div>
                <div className="text-xs uppercase tracking-widest text-amber-300 font-bold mb-1">{isNl ? "Voor designers" : "For designers"}</div>
                <div className="font-bold text-sm leading-tight">{isNl ? "Download Media Kit (PDF)" : "Download Media Kit (PDF)"}</div>
                <p className="text-xs text-white/70 mt-1">{isNl ? "Alle advertentie-afmetingen, formaten en safe zones in 1 PDF." : "All ad sizes, formats and safe zones in 1 PDF."}</p>
              </div>
            </div>
          </a>
        </aside>
      </section>

      {/* Value-prop strip — built for small budgets */}
      <section className="bg-white border-t border-[#E2E8F0]" data-testid="auction-value-prop">
        <div className="hac-container py-14 max-w-5xl">
          <div className="grid md:grid-cols-[1fr_2fr] gap-8 items-start">
            <div>
              <div className="overline mb-2 text-amber-600 inline-flex items-center gap-1.5"><Star size={12} className="fill-current" /> {isNl ? "Voor elke beurs" : "For every budget"}</div>
              <h2 className="font-heading text-3xl md:text-4xl text-[#0F172A] leading-[1.1]">
                {isNl ? "Ook bedoeld voor kleine bedrijven met een beperkt budget" : "Built for small companies with limited ad budgets too"}
              </h2>
            </div>
            <div className="space-y-4 text-[#0F172A]">
              <p className="text-base leading-relaxed">
                {isNl
                  ? "Klassieke ad-platforms zijn voor de meeste paardenapps niet betaalbaar. Daarom doen we het anders: één week per jaar de Featured App van de week, vanaf slechts €25 bieden, met stappen van €25. De richtprijs van €499 is een ankerprijs — geen ondergrens."
                  : "Classic ad platforms aren't affordable for most horse apps. So we do it differently: one week per year as Featured App of the Week, bids start from just €25, in steps of €25. The €499 list price is an anchor — not a minimum."}
              </p>
              <div className="grid sm:grid-cols-3 gap-3">
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <div className="text-[10px] uppercase tracking-widest text-amber-700 font-bold">{isNl ? "Min. bod" : "Min bid"}</div>
                  <div className="text-2xl font-bold text-[#0F172A] mt-1">€25</div>
                  <div className="text-[11px] text-amber-700 mt-1">{isNl ? "ondergrens" : "floor"}</div>
                </div>
                <div className="bg-white border border-[#E2E8F0] rounded-xl p-4">
                  <div className="text-[10px] uppercase tracking-widest text-[#64748B] font-bold">{isNl ? "Bied-stap" : "Increment"}</div>
                  <div className="text-2xl font-bold text-[#0F172A] mt-1">+€25</div>
                  <div className="text-[11px] text-[#64748B] mt-1">{isNl ? "per verhoging" : "per raise"}</div>
                </div>
                <div className="bg-[#0F172A] text-white rounded-xl p-4">
                  <div className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">{isNl ? "Richtprijs" : "Recommended"}</div>
                  <div className="text-2xl font-bold mt-1">€499</div>
                  <div className="text-[11px] text-[#94A3B8] mt-1">{isNl ? "ankerprijs" : "anchor"}</div>
                </div>
              </div>
              <ul className="space-y-2 text-sm">
                <li className="flex gap-2"><CheckCircle2 size={16} className="text-emerald-500 shrink-0 mt-0.5" /> {isNl ? "Als er weinig concurrentie is, kun je de week soms voor een spotprijs winnen." : "When competition is low, you can sometimes win the week at a bargain."}</li>
                <li className="flex gap-2"><CheckCircle2 size={16} className="text-emerald-500 shrink-0 mt-0.5" /> {isNl ? "Eén week featured = directe homepage-positie + 'Featured App of the Week' badge zichtbaar in alle 22 talen." : "One week featured = top homepage placement + 'Featured App of the Week' badge in all 22 languages."}</li>
                <li className="flex gap-2"><CheckCircle2 size={16} className="text-emerald-500 shrink-0 mt-0.5" /> {isNl ? "Eerlijke veiling: hoogste bod wint. Geen abonnement, geen verborgen kosten." : "Fair auction: highest bid wins. No subscriptions, no hidden fees."}</li>
                <li className="flex gap-2"><CheckCircle2 size={16} className="text-emerald-500 shrink-0 mt-0.5" /> {isNl ? "Factuur per email na winst. Geen Stripe-account nodig." : "Invoice by email after winning. No Stripe account required."}</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Hall of Past Winners */}
      {pastWinners.length > 0 && (
        <section className="bg-[#F8FAFC] border-t border-[#E2E8F0]" data-testid="auction-past-winners">
          <div className="hac-container py-16 max-w-6xl">
            <div className="overline mb-2 text-amber-600 inline-flex items-center gap-1.5"><Trophy size={12} /> {isNl ? "Hall of past winners" : "Hall of past winners"}</div>
            <h2 className="font-heading text-3xl md:text-4xl text-[#0F172A] mb-2">{isNl ? "Eerdere featured apps" : "Past featured apps"}</h2>
            <p className="text-sm text-[#64748B] mb-8 max-w-2xl">{isNl ? "Apps die in eerdere weken via veiling de homepage-positie hebben gewonnen." : "Apps that won the homepage spot through previous weekly auctions."}</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {pastWinners.map((w) => (
                <div key={w.id} className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden hover:border-amber-300 transition-colors" data-testid={`past-winner-${w.id}`}>
                  {w.image_url ? (
                    <img src={w.image_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${w.image_url}` : w.image_url} alt={w.title} className="w-full h-32 object-cover" />
                  ) : (
                    <div className="w-full h-32 bg-gradient-to-br from-amber-100 to-amber-50 flex items-center justify-center"><Trophy size={28} className="text-amber-400" /></div>
                  )}
                  <div className="p-4">
                    <div className="text-[10px] uppercase tracking-widest text-amber-700 font-bold mb-1 inline-flex items-center gap-1">{w.winner_company ? <><Star size={9} className="fill-current" /> {w.winner_company}</> : "Featured"}</div>
                    <div className="font-bold text-[#0F172A] text-sm leading-snug line-clamp-2">{w.title}</div>
                    <div className="mt-2 text-[11px] text-[#94A3B8] flex justify-between">
                      <span>{new Date(w.won_on).toLocaleDateString(isNl ? "nl-NL" : "en-GB", { day: "numeric", month: "short", year: "numeric" })}</span>
                      {w.winning_amount_eur && <span className="font-mono text-amber-700 font-semibold">€{Math.round(w.winning_amount_eur)}</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
};

export default AuctionPage;
